import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Button, message, Alert } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { signInApiPortal } from '../../api/portal';
import { ACCESS_TOKEN, REFRESH_TOKEN } from '../../utils/constants';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getAccessTokenApi } from '../../api/auth';
import './LoginForm.scss';

const LoginForm = () => {
  const [loading, setLoading] = useState(false);
  const [throttleCountdown, setThrottleCountdown] = useState(0);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);
  const [waitPrefix, setWaitPrefix] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [form] = Form.useForm();
  const history = useHistory();

  const throttleCountdownRef = useRef(0);
  const rateLimitCountdownRef = useRef(0);
  const submitInFlightRef = useRef(false);

  const { tenantId } = useSelector((store) => store.tenantData);

  useEffect(() => {
    const getData = async () => {
      const token = await getAccessTokenApi();

      if (token) {
        history.push(`/home`);
      }
    };

    getData();
  }, [history]);

  // Countdown para el throttle
  useEffect(() => {
    throttleCountdownRef.current = throttleCountdown;
    if (throttleCountdown <= 0) return undefined;

    const timer = setTimeout(() => {
      setThrottleCountdown((current) => Math.max(0, current - 1));
    }, 1000);

    return () => clearTimeout(timer);
  }, [throttleCountdown]);

  // Countdown para el rate-limit
  useEffect(() => {
    rateLimitCountdownRef.current = rateLimitCountdown;
    if (rateLimitCountdown <= 0) return undefined;

    const timer = setTimeout(() => {
      setRateLimitCountdown((current) => Math.max(0, current - 1));
    }, 1000);

    return () => clearTimeout(timer);
  }, [rateLimitCountdown]);

  // Resetear el wait prefix cuando los countdowns son 0
  useEffect(() => {
    if (throttleCountdown === 0 && rateLimitCountdown === 0) {
      setWaitPrefix(null);
    }
  }, [throttleCountdown, rateLimitCountdown]);

  const isWaiting = throttleCountdown > 0 || rateLimitCountdown > 0;
  const isSubmitDisabled = loading || isWaiting;
  const displayedCountdown = rateLimitCountdown > 0 ? rateLimitCountdown : throttleCountdown;

  const startWait = (seconds, prefix, { isRateLimit, feedbackDuringWait = null }) => {
    setWaitPrefix(prefix);
    setFeedback(feedbackDuringWait);
    if (isRateLimit) {
      setRateLimitCountdown(seconds);
      setThrottleCountdown(0);
    } else {
      setThrottleCountdown(seconds);
      setRateLimitCountdown(0);
    }
  };

  const formatCountdown = (totalSeconds) => {
    const minutes = `${Math.floor(totalSeconds / 60)}m`;
    const seconds = totalSeconds % 60;
    return `${totalSeconds > 60 ? minutes : ''} ${seconds}s`;
  };

  const handleFinish = async (values) => {
    if (
      submitInFlightRef.current ||
      throttleCountdownRef.current > 0 ||
      rateLimitCountdownRef.current > 0
    ) {
      return;
    }

    submitInFlightRef.current = true;
    setLoading(true);
    setFeedback(null);

    try {
      const result = await signInApiPortal({ data: values, tenantId });

      if (result?.accessToken && result?.refreshToken) {
        localStorage.setItem(ACCESS_TOKEN, result.accessToken);
        localStorage.setItem(REFRESH_TOKEN, result.refreshToken);
        message.success('Login correcto');
        history.push(`/home`);
        return;
      }

      if (result?.code === 201) {
        setFeedback({
          type: 'warning',
          message: result.message,
        });
        return;
      }

      if (result?.retryAfter > 0) {
        const seconds = Math.max(1, Number(result.retryAfter) || 60);
        startWait(seconds, 'Demasiados intentos fallidos. Reintentá en', { isRateLimit: true });
        return;
      }

      if (result?.throttleSeconds > 0) {
        const seconds = Math.max(1, Number(result.throttleSeconds) || 1);
        const feedbackDuringWait = result.message
          ? {
              type: 'error',
              message: result.message,
              description: result.hint || result.warning,
            }
          : result.warning
            ? {
                type: 'warning',
                message: result.warning,
              }
            : null;

        startWait(seconds, 'Demasiados intentos fallidos. Reintentá en', {
          isRateLimit: false,
          feedbackDuringWait,
        });
        return;
      }

      if (result?.message) {
        setFeedback({
          type: 'error',
          message: result.message,
          description: result.hint,
        });
        return;
      }

      if (result?.warning) {
        setFeedback({
          type: 'warning',
          message: result.warning,
        });
      }
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
    }
  };

  const alertProps = (() => {
    if (isWaiting && waitPrefix) {
      const waitDescription = feedback?.description;
      return {
        type: 'warning',
        message: `${waitPrefix} ${formatCountdown(displayedCountdown)}`,
        description: waitDescription || undefined,
      };
    }

    if (feedback) {
      return {
        type: feedback.type,
        message: feedback.message,
        description: feedback.description,
      };
    }

    return null;
  })();

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish}>
      {alertProps && (
        <Alert
          type={alertProps.type}
          showIcon
          message={alertProps.message}
          description={alertProps.description}
          style={{ marginBottom: 16 }}
        />
      )}

      <Form.Item
        name="dni"
        getValueFromEvent={(event) => String(event?.target?.value ?? '').replace(/\D/g, '')}
        rules={[
          {
            validator: (_, value) => {
              const text = String(value ?? '');
              if (!text || !/^\d+$/.test(text)) {
                return Promise.reject(new Error('Ingresá el DNI sin puntos'));
              }
              if (text.length < 5) {
                return Promise.reject(new Error('DNI inválido'));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input
          prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
          placeholder="DNI"
          className="login-form__dni"
          size="large"
          type="number"
          inputMode="numeric"
          disabled={isWaiting}
          onChange={() => setFeedback(null)}
          onKeyDown={(event) => {
            if (['.', ',', 'e', 'E', '+', '-'].includes(event.key)) {
              event.preventDefault();
            }
          }}
          onBeforeInput={(event) => {
            if (event.data && /\D/.test(event.data)) {
              event.preventDefault();
            }
          }}
        />
      </Form.Item>

      <Form.Item
        name="password"
        rules={[
          {
            required: true,
            message: 'Debe ingresar una contraseña',
          },
        ]}
      >
        <Input.Password
          prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
          placeholder="Contraseña"
          size="large"
          disabled={isWaiting}
          onChange={() => setFeedback(null)}
        />
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          style={{ width: '100%' }}
          loading={loading}
          disabled={isSubmitDisabled}
          size="large"
        >
          Iniciar sesión
        </Button>
      </Form.Item>
    </Form>
  );
};

export default LoginForm;
